import { useState } from "react";

import { CheckIcon, ChevronDownIcon, SparklesIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

import { DEMO_ACCOUNTS, DemoAccount } from "@/features/auth/fixtures/demoAccounts";
import { ROLE_CONFIG } from "@/features/members/config";
import { cn } from "@/shared/lib/utils/cn";

export type { DemoAccount };

interface DemoAccountPanelProps {
  account?: DemoAccount | null;
  onSelect: (account: DemoAccount) => void;
}

interface DemoAccountCardProps {
  account: DemoAccount;
  isSelected: boolean;
  onSelect: (account: DemoAccount) => void;
}

export default function DemoAccountPanel(props: DemoAccountPanelProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-col gap-0 border border-dashed border-border">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/60">
        <div className="flex items-center gap-2">
          <SparklesIcon className="size-3.5 text-primary" />
          <span className="text-xs font-semibold">Try a demo account</span>
          <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
            {DEMO_ACCOUNTS.length} accounts
          </Badge>
        </div>
        <ChevronDownIcon className={cn("size-3.5 text-muted-foreground transition-transform duration-200", open && "rotate-180")} />
      </button>
      {open && (
        <>
          <Separator />
          <div className="flex flex-col gap-4 p-4">
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              Each account holds different roles across a few projects, so pick one to match how much you want to change. Anything you do here is{" "}
              <span className="font-semibold text-foreground">reset periodically</span>, so explore freely. Select an account below to pre-fill the login form.
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {DEMO_ACCOUNTS.map((a) => (
                <DemoAccountCard key={a.email} account={a} isSelected={props.account?.email === a.email} onSelect={props.onSelect} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function DemoAccountCard(props: DemoAccountCardProps) {
  return (
    <button
      type="button"
      onClick={() => props.onSelect(props.account)}
      className={cn("group flex w-full flex-col gap-3 border p-3 text-left transition-all duration-150", props.isSelected ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "border-border bg-card hover:border-primary/40 hover:bg-primary/5")}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-semibold text-foreground">{props.account.name}</span>
          <span className="text-[10px] text-muted-foreground">{props.account.email}</span>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Badge variant="outline" className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
            <SparklesIcon className="size-2.5 text-primary" />
            Demo
          </Badge>
          <span className={cn("flex size-4 items-center justify-center bg-primary text-primary-foreground transition-all duration-150", props.isSelected ? "opacity-100 scale-100" : "opacity-0 scale-75")}>
            <CheckIcon className="size-2.5" />
          </span>
        </div>
      </div>
      <div className="flex flex-wrap gap-1">
        {props.account.projects.map((p) => {
          const cfg = ROLE_CONFIG[p.role];
          return (
            <span key={p.name} className={cn("flex items-center gap-1 border px-1.5 py-0.5 text-[10px]", cfg.badgeClass)}>
              <cfg.icon className={cn("size-2.5 shrink-0", cfg.className)} />
              {p.name}
              <span className="text-muted-foreground">· {p.role}</span>
            </span>
          );
        })}
      </div>
      <p className="text-[10px] leading-relaxed text-muted-foreground">{props.account.note}</p>
      <span className={cn("text-[10px] font-semibold transition-colors duration-150", props.isSelected ? "text-primary" : "text-primary opacity-0 group-hover:opacity-100")}>{props.isSelected ? "✓ Selected - click Sign in above" : "Click to use this account →"}</span>
    </button>
  );
}
